import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { Divider, Stack, Typography, alpha } from '@mui/material';
import { Routes } from 'enums';
import { MainFolder } from 'features/lead/lead-management/lead-manage-questionnaires/lead-manage-questionnaires.types';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import { Link } from 'react-router';
import { makeStyles } from 'tss-react/mui';

interface Props {
  loadingFolders: boolean;
  leadTagFolders: MainFolder[];
  selectedFolder: string;
  searchFieldFolders: string;

  handleFolderSearchFieldChange(event: React.ChangeEvent<HTMLInputElement>): void;

  searchKeyDown(): void;

  folderSearchKeyUp(): void;

  handleSelectFolder(folderId: string): () => void;

  handleOpenAddLeadTagFolderDialog(): void;

  handleOpenAddLeadTagSubFolderDialog(folderId: string): () => void;

  createQuestionnaire(leadTagFolderId: string): () => void;
}

interface FolderItemProps {
  folder: MainFolder;
  selectedFolder: string;

  handleSelectFolder(folderId: string): () => void;

  handleOpenAddLeadTagSubFolderDialog(folderId: string): () => void;
}

const FolderItem: React.FC<FolderItemProps> = ({
  folder,
  selectedFolder,
  handleSelectFolder,
  handleOpenAddLeadTagSubFolderDialog,
}) => {
  const { classes, cx } = useStyles();

  const selected = selectedFolder === folder.id;

  return (
    <>
      <Stack
        className={cx(classes.folder, selected ? 'selected' : undefined)}
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        onClick={handleSelectFolder(folder.id)}
      >
        <Stack direction={'row'} gap={2}>
          {!selected ? <FolderIcon className={classes.icon} /> : <FolderOpenIcon className={classes.icon} />}
          <Typography className={classes.name}>{folder.name}</Typography>
        </Stack>
        <Stack className={'folder-actions'} direction={'row'} gap={2}>
          <PaiLink text={'+ Unterordner'} variant={'primary'} size={'xs'} action={handleOpenAddLeadTagSubFolderDialog(folder.id)} />
          <Link className={classes.link} to={`${Routes.LEAD_CREATION_DETAILS}?leadTagFolderId=${folder.id}`} target={'_blank'}>
            <PaiLink text={'+ Umfrage'} variant={'primary'} size={'xs'} />
          </Link>
        </Stack>
      </Stack>
      <Stack className={classes.subFolders}>
        {folder.subFolders.map((subFolder) => (
          <Stack
            key={subFolder.id}
            className={cx(classes.folder, 'sub-folder', selectedFolder === subFolder.id ? 'selected' : undefined)}
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            onClick={handleSelectFolder(subFolder.id)}
          >
            <Stack direction={'row'} gap={2}>
              <Stack direction={'row'} alignItems={'center'} gap={1}>
                <Divider className={classes.connection} />
                {selectedFolder !== subFolder.id ? <FolderIcon className={classes.icon} /> : <FolderOpenIcon className={classes.icon} />}
              </Stack>
              <Typography className={classes.name}>{subFolder.name}</Typography>
            </Stack>
            <Stack className={'folder-actions'} direction={'row'} gap={2}>
              <Link className={classes.link} to={`${Routes.LEAD_CREATION_DETAILS}?leadTagFolderId=${subFolder.id}`} target={'_blank'}>
                <PaiLink text={'+ Umfrage'} variant={'primary'} size={'xs'} />
              </Link>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </>
  );
};

const LeadFolders: React.FC<Props> = ({
  loadingFolders,
  leadTagFolders,
  selectedFolder,
  searchFieldFolders,
  handleFolderSearchFieldChange,
  searchKeyDown,
  folderSearchKeyUp,
  handleSelectFolder,
  handleOpenAddLeadTagFolderDialog,
  handleOpenAddLeadTagSubFolderDialog,
}) => {
  const { classes } = useStyles();

  return (
    <Stack className={classes.folderContent} gap={2}>
      <Stack className={classes.folderSearch} gap={2}>
        <PaiTextField
          value={searchFieldFolders}
          label={'Suche'}
          placeholder={'Suche nach Ordnern'}
          disableBorder={true}
          onChange={handleFolderSearchFieldChange}
          onKeyDown={searchKeyDown}
          onKeyUp={folderSearchKeyUp}
        />
        <Stack direction={'row'} justifyContent={'center'}>
          <PaiButton text={'+ Ordner'} action={handleOpenAddLeadTagFolderDialog} />
        </Stack>
      </Stack>
      <div className={classes.folders}>
        {loadingFolders ? (
          <PaiDotLoader />
        ) : (
          <Stack>
            {leadTagFolders.map((folder) => (
              <FolderItem
                key={folder.id}
                folder={folder}
                selectedFolder={selectedFolder}
                handleSelectFolder={handleSelectFolder}
                handleOpenAddLeadTagSubFolderDialog={handleOpenAddLeadTagSubFolderDialog}
              />
            ))}
          </Stack>
        )}
      </div>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  folderContent: {
    height: 'calc(100% - 19.5px - 32px)',
  },
  folderSearch: {
    width: '100%',
    padding: '16px',
    border: `2px solid ${theme.palette.backgrounds.main}`,
  },
  folders: {
    maxHeight: 'calc(100% - 16px - 150.5px)',
    overflow: 'auto',
    marginRight: '-16px',
    paddingRight: '10px',
  },
  subFolders: {
    paddingLeft: '12px',
  },
  folder: {
    cursor: 'pointer',
    padding: '8px 8px 8px 4px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
    '&:first-of-type': {
      borderTop: `1px solid ${theme.palette.greys.light}`,
    },
    '&.sub-folder': {
      padding: '8px 8px 8px 0',
      borderTop: 'none',
      borderBottom: `1px dotted ${theme.palette.greys.light}`,
      borderLeft: `1px dotted ${theme.palette.greys.light}`,
    },
    '& .folder-actions': {
      visibility: 'hidden',
    },
    '&.selected': {
      background: theme.palette.backgrounds.main,
      '& .folder-actions': {
        visibility: 'visible',
      },
    },
    '&:hover': {
      background: alpha('#000', 0.05),
      '& .folder-actions': {
        visibility: 'visible',
      },
    },
  },
  connection: {
    width: '16px',
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.primary.main,
  },
  name: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  action: {
    fontSize: theme.typography.pxToRem(13),
  },
  link: {
    textDecoration: 'none',
  },
}));

export default LeadFolders;
